import React, { useRef, useCallback, useEffect, useState } from "react";
import { bulkUploadEntityHasProblems } from "../utils/bulkUploadOutcomeHelpers";
import type { BulkUploadIssueRowEntity } from "../../domain/entities/BulkUploadEntity";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Accordion } from "../../../../ui/components/Accordion";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import {
  FeatherAlertTriangle,
  FeatherCheckCircle,
  FeatherDownload,
  FeatherInfo,
  FeatherUpload,
  FeatherUploadCloud,
  FeatherX,
  FeatherXCircle,
} from "@subframe/core";
import { useBulkUpload, type BulkUploadKind } from "../hooks/useBulkUpload";
import {
  useToast,
  ReviewRequestModal,
} from "../../../../shared/presentation/components";
import type { ChangeDetails } from "../../../../shared/utils/reviewWorkflow";
import {
  BulkVerifyForReviewError,
  type BulkVerifyForReviewFailure,
} from "../../data/api/bulkUploadApi";
import { formatBulkUploadFieldLabel } from "../../../../shared/presentation/utils/bulkUploadFieldLabels";
import { getVerificationConfig } from "../../../../shared/data/api/gstinVerificationApi";
import { getCompany } from "../../../../shared/data/storage/authStorage";
import {
  GstinBulkVerifyModal,
  type GstinBulkVerifyRow,
  type GstinRowVerification,
} from "./GstinBulkVerifyModal";

export interface BulkUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: BulkUploadKind;
  /** When true, file is validated and a review request is opened instead of live import. */
  requiresReview?: boolean;
  moduleCode?: string;
  entityType?: string;
  onUploadComplete?: () => void;
}

const VALID_EXTENSIONS = [".xlsx", ".xls"];

export const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  open,
  onOpenChange,
  kind,
  requiresReview = false,
  moduleCode,
  entityType,
  onUploadComplete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const resultsAnchorRef = useRef<HTMLDivElement>(null);
  const { showError, showSuccess, showWarning } = useToast();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewChangeDetails, setReviewChangeDetails] =
    useState<ChangeDetails | null>(null);
  const [gstinVerifyOpen, setGstinVerifyOpen] = useState(false);
  const [gstinVerifyChangeDetails, setGstinVerifyChangeDetails] =
    useState<ChangeDetails | null>(null);
  const gstinVerificationRequiredRef = useRef<boolean | null>(null);
  const {
    selectedFile,
    isUploading,
    isDownloading,
    result,
    error,
    verifyFailures,
    selectFile,
    downloadTemplate,
    upload,
    verifyForReview,
    reset,
  } = useBulkUpload(kind);

  const title =
    kind === "customers"
      ? "Bulk upload customers"
      : kind === "vendors"
        ? "Bulk upload vendors"
        : "Bulk upload GLs";
  const entityLabel =
    kind === "customers"
      ? "customers"
      : kind === "vendors"
        ? "vendors"
        : "GLs";

  const handleClose = useCallback(
    (next: boolean) => {
      if (!next) {
        reset();
      }
      onOpenChange(next);
    },
    [onOpenChange, reset],
  );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!VALID_EXTENSIONS.includes(ext)) {
      showError("Only Excel files (.xlsx, .xls) are allowed");
      return;
    }
    selectFile(file);
  };

  const handleDownload = async () => {
    try {
      await downloadTemplate();
    } catch {
      showError("Could not download template");
    }
  };

  const resultHasProblems = result
    ? bulkUploadEntityHasProblems(result)
    : false;
  const hasResultIssues =
    Boolean(error) || resultHasProblems || verifyFailures.length > 0;

  useEffect(() => {
    if (!open || !hasResultIssues) return;
    const timer = window.setTimeout(() => {
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }
      resultsAnchorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [open, hasResultIssues, result, error, verifyFailures]);

  const otherIssueRows = result?.otherFailures ?? [];
  const otherErrorsCount = result ? result.otherFailedCount : 0;

  const shouldVerifyGstinsForReview = async (
    cd: ChangeDetails,
  ): Promise<boolean> => {
    if (Number(getCompany()?.companyGstEnabled ?? 0) !== 1) return false;
    if (businessGstReviewRows(cd).length === 0) return false;
    if (gstinVerificationRequiredRef.current === null) {
      try {
        const config = await getVerificationConfig();
        gstinVerificationRequiredRef.current =
          config.required && config.has_credentials;
      } catch (err) {
        console.error("Error fetching verification config:", err);
        // Default to not required if config fetch fails
        gstinVerificationRequiredRef.current = false;
      }
    }
    return gstinVerificationRequiredRef.current;
  };

  const handleGstinVerifyComplete = (
    stamped: Record<number, GstinRowVerification>,
  ) => {
    const cd = gstinVerifyChangeDetails;
    setGstinVerifyOpen(false);
    setGstinVerifyChangeDetails(null);
    if (!cd) return;
    for (const row of bulkReviewRows(cd)) {
      const verification = stamped[row.excel_row];
      if (verification) {
        (
          row as GstinBulkVerifyRow & {
            gstin_verification?: GstinRowVerification;
          }
        ).gstin_verification = verification;
      }
    }
    setReviewChangeDetails(cd);
    handleClose(false);
    setReviewOpen(true);
  };

  const handleGstinVerifyCancel = () => {
    setGstinVerifyOpen(false);
    setGstinVerifyChangeDetails(null);
  };

  const handleUploadClick = async () => {
    if (!selectedFile) return;

    if (requiresReview && moduleCode && entityType) {
      try {
        const cd = await verifyForReview();
        if (await shouldVerifyGstinsForReview(cd)) {
          setGstinVerifyChangeDetails(cd);
          setGstinVerifyOpen(true);
          return;
        }
        setReviewChangeDetails(cd);
        handleClose(false);
        setReviewOpen(true);
      } catch (e) {
        if (e instanceof BulkVerifyForReviewError) {
          showError(
            e.message ||
              "Verification failed: some rows are not valid. Review the list below.",
          );
        } else {
          showError(e instanceof Error ? e.message : "Validation failed");
        }
      }
      return;
    }

    try {
      const res = await upload();
      if (!res) return;
      const problems = bulkUploadEntityHasProblems(res);
      const created = res.createdCount ?? res.insertedCount;

      if (created > 0 && !problems) {
        showSuccess(`Imported ${created} ${entityLabel}. All rows succeeded.`);
        onUploadComplete?.();
        handleClose(false);

        return;
      }

      if (created > 0 && problems) {
        showWarning(
          `Imported ${created} ${entityLabel}; some rows need attention below.`,
        );

        return;
      }

      if (problems) {
        showError("No rows were imported. Review the outcomes below.");

        return;
      }

      showError("No rows were imported. Adjust the file and try again.");
    } catch {
      showError("Upload failed");
    }
  };

  return (
    <>
    <DialogLayout
      open={open}
      onOpenChange={handleClose}
      className="flex max-h-[90vh] w-full min-w-[min(960px,100vw-2rem)] max-w-[960px] flex-col overflow-hidden"
    >
      <div className="flex max-h-[90vh] w-full flex-col overflow-hidden">
        <div className="flex w-full flex-none items-start justify-between gap-4 border-b border-solid border-neutral-border p-6">
          <div className="flex flex-col items-start gap-1 pr-8">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {title}
            </span>
            <span className="text-body font-body text-subtext-color">
              Download the template, fill it in Excel, then upload. Use the same
              column headers as the template.
            </span>
          </div>
          <IconButton
            size="small"
            icon={<FeatherX />}
            onClick={() => handleClose(false)}
            disabled={isUploading}
          />
        </div>

        <div
          ref={scrollContainerRef}
          className="flex min-h-0 w-full flex-1 flex-col gap-6 overflow-y-auto p-6"
        >
          <div className="flex flex-col gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
            <span className="text-body-bold font-body-bold text-default-font">
              Steps
            </span>
            <ol className="list-decimal space-y-2 pl-5 text-body font-body text-default-font">
              <li>
                Download the Excel template (headers and validations match the
                system).
              </li>
              <li>
                Fill one row per{" "}
                {kind === "customers"
                  ? "customer"
                  : kind === "vendors"
                    ? "vendor"
                    : "GL"}
                . Do not change header row text.
              </li>
              <li>
                Save as .xlsx or .xls, then upload here. Wait until processing
                finishes.
              </li>
              {requiresReview ? (
                <li>
                  Your file is checked for errors first. If it passes, you send a
                  review request; your manager approves before records are
                  created.
                </li>
              ) : null}
            </ol>
          </div>

          <div className="flex flex-col gap-3">
            <div
              className="flex items-start gap-3 rounded-md border border-solid border-brand-200 bg-brand-50 px-4 py-3"
              role="note"
            >
              <IconWithBackground
                variant="brand"
                size="small"
                icon={<FeatherInfo />}
              />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-body-bold font-body-bold text-brand-900">
                  Keep every sheet in the workbook
                </span>
                <span className="text-caption font-caption text-brand-800">
                  Do not rename or delete any sheet from the downloaded template.
                  The upload expects the original sheet names and structure,
                  including hidden lookup sheets used for validation.
                </span>
              </div>
            </div>

            <span className="text-body-bold font-body-bold text-default-font">
              1. Template
            </span>
            <Button
              variant="neutral-secondary"
              icon={<FeatherDownload />}
              onClick={() => void handleDownload()}
              loading={isDownloading}
              disabled={isDownloading || isUploading}
            >
              Download template
            </Button>
          </div>

          <div className="flex flex-col gap-3">
            <span className="text-body-bold font-body-bold text-default-font">
              2. Upload file
            </span>
            <button
              type="button"
              className="flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-md border border-dashed border-brand-600 bg-brand-50 px-6 py-8"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <IconWithBackground size="large" icon={<FeatherUploadCloud />} />
              <div className="flex flex-col items-center gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  {selectedFile
                    ? selectedFile.name
                    : "Click to select Excel file"}
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  .xlsx or .xls
                </span>
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleFileSelect}
            />
            {selectedFile && (
              <div className="flex justify-end gap-2">
                <Button
                  variant="neutral-tertiary"
                  onClick={() => {
                    selectFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  disabled={isUploading}
                >
                  Clear file
                </Button>
                <Button
                  icon={<FeatherUpload />}
                  onClick={() => void handleUploadClick()}
                  loading={isUploading}
                  disabled={isUploading}
                >
                  {isUploading
                    ? requiresReview
                      ? "Checking…"
                      : "Uploading…"
                    : requiresReview
                      ? "Submit for review"
                      : "Upload"}
                </Button>
              </div>
            )}
          </div>

          {error && verifyFailures.length === 0 && (
            <div className="flex items-start gap-3 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
              <FeatherAlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-error-600" />
              <span className="text-body font-body text-error-700">
                {error}
              </span>
            </div>
          )}

          {verifyFailures.length > 0 ? (
            <div className="flex flex-col gap-3 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
              <div className="flex items-start gap-2 text-body-bold font-body-bold text-error-800">
                <FeatherAlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-error-600" />
                <div className="flex flex-col gap-1">
                  <span>Verification failed: some rows are not valid</span>
                  <span className="text-caption font-caption font-normal text-error-700">
                    {verifyFailures.length} row
                    {verifyFailures.length !== 1 ? "s" : ""} need attention.
                    Fix them in Excel and submit again.
                  </span>
                </div>
              </div>
              <Accordion
                className="w-full overflow-hidden rounded-md border border-solid border-error-300 bg-white"
                trigger={
                  <div className="flex w-full items-center gap-2 px-4 py-3">
                    <span className="text-body-bold font-body-bold text-error-800">
                      Invalid rows ({verifyFailures.length})
                    </span>
                    <Accordion.Chevron className="ml-auto text-error-800" />
                  </div>
                }
                defaultOpen
              >
                <div className="flex max-h-64 flex-col gap-3 overflow-y-auto border-t border-solid border-error-200 px-4 py-3">
                  {verifyFailures.map((rowFailure, rowIndex) => (
                    <div
                      key={`${rowFailure.excel_row}-${rowIndex}`}
                      className="flex flex-col gap-1 border-b border-solid border-error-100 pb-3 last:border-0 last:pb-0"
                    >
                      <span className="text-body-bold font-body-bold text-error-800">
                        {bulkVerifyFailurePrimaryLine(rowFailure)}
                      </span>
                      <ul className="list-disc space-y-1 pl-5 text-body font-body text-error-800">
                        {rowFailure.errors.map((err, i) => (
                          <li key={i}>
                            <span className="font-semibold">
                              {formatBulkUploadFieldLabel(err.field)}:
                            </span>{" "}
                            {err.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Accordion>
            </div>
          ) : null}

          {result ? (
            <div className="flex flex-col gap-4 rounded-md border border-solid border-neutral-border p-4">
              <div className="flex items-center gap-2">
                {(() => {
                  if (result.insertedCount > 0 && !resultHasProblems) {
                    return (
                      <FeatherCheckCircle className="h-5 w-5 text-success-600" />
                    );
                  }
                  if (result.insertedCount > 0 && resultHasProblems) {
                    return (
                      <FeatherAlertTriangle className="h-5 w-5 text-warning-600" />
                    );
                  }
                  return <FeatherXCircle className="h-5 w-5 text-error-600" />;
                })()}
                <span className="text-body-bold font-body-bold text-default-font">
                  Results
                </span>
              </div>

              <p className="text-caption font-caption text-subtext-color">
                {result.processedRows} non-empty row
                {result.processedRows !== 1 ? "s" : ""} processed out of{" "}
                {result.totalRows} in the sheet · {result.validatedSuccessful}{" "}
                passed checks before attempting import
              </p>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Stat
                  label="Created"
                  value={result.createdCount}
                  accent="success"
                />
                <Stat
                  label="Already in system"
                  value={result.alreadyExistsCount}
                />
                <Stat
                  label="Fix in Excel"
                  value={result.validationFailedCount}
                  accent={
                    result.validationFailedCount > 0 ? "error" : undefined
                  }
                />
                <Stat
                  label="Other errors"
                  value={otherErrorsCount}
                  accent={otherErrorsCount > 0 ? "error" : undefined}
                />
              </div>

              <div className="flex flex-col gap-2 rounded-md bg-neutral-50 px-3 py-2 text-caption font-caption text-subtext-color">
                <span>
                  Valid rows are saved as you upload; problematic rows appear in
                  the sections below.
                </span>
              </div>

              <div className="flex flex-col gap-2">
                {result.createdRecords.length > 0 ? (
                  <Accordion
                    className="w-full overflow-hidden rounded-md border border-solid border-success-200 bg-success-50"
                    trigger={
                      <div className="flex w-full items-center gap-2 px-4 py-3">
                        <span className="text-body-bold font-body-bold text-success-900">
                          Created ({result.createdRecords.length})
                        </span>
                        <Accordion.Chevron className="ml-auto text-success-900" />
                      </div>
                    }
                    defaultOpen
                  >
                    <div className="border-t border-solid border-success-200 px-4 py-3">
                      <OutcomeIssueScroller>
                        {result.createdRecords.map((r, i) => (
                          <div key={i} className="flex gap-2 py-1 text-sm">
                            <FeatherCheckCircle className="h-4 w-4 flex-shrink-0 text-success-600" />
                            <span>
                              Row {r.row}: {r.displayName || "—"}
                            </span>
                          </div>
                        ))}
                      </OutcomeIssueScroller>
                    </div>
                  </Accordion>
                ) : null}

                {result.skippedExisting.length > 0 ? (
                  <Accordion
                    className="w-full overflow-hidden rounded-md border border-solid border-neutral-border bg-neutral-50"
                    trigger={
                      <div className="flex w-full items-center gap-2 px-4 py-3">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Already in system ({result.skippedExisting.length})
                        </span>
                        <Accordion.Chevron className="ml-auto" />
                      </div>
                    }
                    defaultOpen={
                      result.insertedCount === 0 &&
                      result.skippedExisting.length > 0 &&
                      result.validationFailures.length === 0 &&
                      otherIssueRows.length === 0
                    }
                  >
                    <div className="border-t border-solid border-neutral-border px-4 py-3">
                      <BulkUploadIssueBlocks rows={result.skippedExisting} />
                    </div>
                  </Accordion>
                ) : null}

                {result.validationFailures.length > 0 ? (
                  <Accordion
                    className="w-full overflow-hidden rounded-md border border-solid border-error-200 bg-error-50"
                    trigger={
                      <div className="flex w-full items-center gap-2 px-4 py-3">
                        <span className="text-body-bold font-body-bold text-error-800">
                          Fix in Excel ({result.validationFailures.length})
                        </span>
                        <Accordion.Chevron className="ml-auto text-error-800" />
                      </div>
                    }
                    defaultOpen={!!result.validationFailures.length}
                  >
                    <div className="border-t border-solid border-error-200 px-4 py-3">
                      <BulkUploadIssueBlocks rows={result.validationFailures} />
                    </div>
                  </Accordion>
                ) : null}

                {otherIssueRows.length > 0 ? (
                  <Accordion
                    className="w-full overflow-hidden rounded-md border border-solid border-error-200 bg-error-50"
                    trigger={
                      <div className="flex w-full items-center gap-2 px-4 py-3">
                        <span className="text-body-bold font-body-bold text-error-800">
                          Other errors ({otherErrorsCount})
                        </span>
                        <Accordion.Chevron className="ml-auto text-error-800" />
                      </div>
                    }
                    defaultOpen={false}
                  >
                    <div className="border-t border-solid border-error-200 px-4 py-3">
                      <BulkUploadIssueBlocks rows={otherIssueRows} />
                    </div>
                  </Accordion>
                ) : null}
              </div>
            </div>
          ) : null}
          <div
            ref={resultsAnchorRef}
            aria-hidden
            className="h-0 w-full shrink-0"
          />
        </div>
      </div>
    </DialogLayout>

    <ReviewRequestModal
      open={reviewOpen}
      onOpenChange={(next) => {
        setReviewOpen(next);
        if (!next) {
          setReviewChangeDetails(null);
        }
      }}
      moduleCode={moduleCode ?? "customer"}
      actionCode="bulk_upload"
      entityType={entityType ?? "customer"}
      entityId={0}
      documentNumber={(() => {
        const raw = reviewChangeDetails?.raw_json as
          | { rows?: unknown[] }
          | undefined;
        const list = raw?.rows;
        return Array.isArray(list) ? `${list.length} rows` : undefined;
      })()}
      changeDetails={reviewChangeDetails ?? undefined}
      skipNavigation
      title={
        kind === "customers"
          ? "Request review: bulk customer import"
          : kind === "vendors"
            ? "Request review: bulk vendor import"
            : "Request review: bulk GL import"
      }
      onSuccess={() => {
        onUploadComplete?.();
      }}
    />

    <GstinBulkVerifyModal
      open={gstinVerifyOpen}
      kind={kind}
      rows={bulkReviewRows(gstinVerifyChangeDetails)}
      onCancel={handleGstinVerifyCancel}
      onComplete={handleGstinVerifyComplete}
    />
    </>
  );
};

function OutcomeIssueScroller({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex max-h-52 flex-col overflow-y-auto text-sm">
      {children}
    </div>
  );
}

function BulkUploadIssueBlocks({ rows }: { rows: BulkUploadIssueRowEntity[] }) {
  return (
    <div className="flex max-h-52 flex-col gap-2 overflow-y-auto text-sm">
      {rows.map((err, i) => (
        <div
          key={i}
          className="flex gap-2 border-b border-solid border-neutral-border/80 py-2 last:border-0"
        >
          <FeatherAlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-error-600" />
          <div className="flex flex-col gap-1">
            <div className="font-medium text-error-800">
              {bulkUploadIssuePrimaryLine(err)}
            </div>
            {err.message?.trim() ? (
              <div className="text-body text-error-700">{err.message}</div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function bulkUploadIssuePrimaryLine(row: BulkUploadIssueRowEntity): string {
  const nm = row.displayName?.trim();
  return nm ? `Row ${row.row} — ${nm}` : `Row ${row.row}`;
}

function bulkVerifyFailurePrimaryLine(row: BulkVerifyForReviewFailure): string {
  if (row.excel_row <= 0) {
    return "File";
  }
  const nm = row.display_name?.trim();
  return nm ? `Row ${row.excel_row} — ${nm}` : `Row ${row.excel_row}`;
}

function bulkReviewRows(cd: ChangeDetails | null): GstinBulkVerifyRow[] {
  const raw = cd?.raw_json as { rows?: unknown[] } | undefined;
  const list = raw?.rows;
  return Array.isArray(list) ? (list as GstinBulkVerifyRow[]) : [];
}

function businessGstReviewRows(cd: ChangeDetails | null): GstinBulkVerifyRow[] {
  return bulkReviewRows(cd).filter(
    (row) => row.payload?.gst_treatment === "business_gst",
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: "success" | "error";
}) {
  const color =
    accent === "success"
      ? "text-success-600"
      : accent === "error"
        ? "text-error-600"
        : "text-default-font";
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-caption font-caption text-subtext-color">
        {label}
      </span>
      <span className={`text-body-bold font-body-bold ${color}`}>{value}</span>
    </div>
  );
}
